import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { debounceTime } from 'rxjs/operators';
import { FormControl } from '@angular/forms';
import { environment } from 'src/environments/local/environment';
import { Countries } from 'src/app/app.consts';
import { LocaleService } from 'src/app/services/locale.service';
import { TranslateService } from '@ngx-translate/core';
import { CurrencyInputMinimumValidator } from 'src/app/validators/currencyInputMinimum';
import { Subscription } from 'rxjs';
import { ActivatedRoute } from '@angular/router';

interface IConfig {
  behaviour: string;
  connect: string;
  range: object;
}

export enum SliderTypes {
  numeric = 'numeric',
  date = 'date',
}

type RangeConfig = {
  behaviour: string;
  connect: string;
  range: { [key: string]: number | number[] };
};

@Component({
  selector: 'app-slider',
  templateUrl: './slider.component.html',
  styleUrls: ['./slider.component.scss'],
})
export class SliderComponent implements OnInit, OnDestroy {
  value!: number | number[];
  numericConfig: RangeConfig = {
    behaviour: 'drag',
    connect: 'lower',
    range: {
      min: [1000, 500],
      '10%': [5000, 1000],
      '25%': [10000, 2500],
      '40%': [20000, 5000],
      '75%': [100_000, 25000],
      max: [350_000],
    },
  };
  minNumericValue = 1000;
  maxNumericValue = 350_000;
  private dateConfig = {
    behaviour: 'drag',
    connect: 'lower',
    range: {
      min: [1, 1],
      max: 36,
    },
  };
  maxDateValue = 36;
  inputFocused = false;
  sliderTypes = SliderTypes;
  switchedToInput = false;
  queryParamSub!: Subscription;
  @Input() header!: string;
  @Input() control!: FormControl;
  @Input() type: SliderTypes = SliderTypes.numeric;
  @Input() error!: string | null;
  @Input() switchToInputText: boolean = true;

  constructor(
    public locale: LocaleService,
    public translate: TranslateService,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.setSliderConfig(environment.country);

    this.control.valueChanges
      .pipe(debounceTime(300))
      .subscribe((value: number) => {
        this.control.setValue(value, { onlySelf: true, emitEvent: false });
      });

    this.queryParamSub = this.route.queryParams.subscribe((params) => {
      const { max_amount } = params;
      if (max_amount) {
        const maxAmount = parseInt(max_amount, 10);
        const roundToFiveHundreds = (value: number) =>
          Math.round(value / 500) * 500;
        const getStep = (value: number) => (value > 10000 ? 1000 : 500);
        const values = [
          roundToFiveHundreds(maxAmount * 0.05),
          roundToFiveHundreds(maxAmount * 0.1),
          roundToFiveHundreds(maxAmount * 0.2),
          roundToFiveHundreds(maxAmount * 0.3),
          roundToFiveHundreds(maxAmount * 0.55),
          roundToFiveHundreds(maxAmount * 0.75),
        ];

        this.numericConfig.range = {
          min: [values[0], getStep(values[0])],
          '10%': [values[1], getStep(values[1])],
          '20%': [values[2], getStep(values[2])],
          '30%': [values[3], getStep(values[3])],
          '55%': [values[4], getStep(values[4])],
          '75%': [values[5], getStep(values[5])],
          max: [maxAmount],
        };
        this.maxNumericValue = roundToFiveHundreds(maxAmount * 0.5);
      }
    });
  }

  setSliderConfig(country: string): void {
    if (country === Countries.SWEDEN) {
      this.minNumericValue = 10_000;
      this.maxNumericValue = 10_000_000;
      this.maxDateValue = 60;
      this.numericConfig = {
        behaviour: 'drag',
        connect: 'lower',
        range: {
          min: [10_000, 5000],
          '10%': [50_000, 10_000],
          '20%': [100_000, 25_000],
          '30%': [200_000, 50_000],
          '55%': [10_00_000, 250_000],
          '75%': [5_000_000, 500_000],
          max: [10_000_000],
        },
      };
      this.dateConfig = {
        behaviour: 'drag',
        connect: 'lower',
        range: {
          min: [1, 1],
          max: 60,
        },
      };
    } else if (country === Countries.NETHERLANDS) {
      this.numericConfig = {
        behaviour: 'drag',
        connect: 'lower',
        range: {
          min: [1000, 500],
          '10%': [5000, 1000],
          '20%': [10000, 2500],
          '30%': [20000, 5000],
          '45%': [100_000, 10000],
          '70%': [1_000_000, 50000],
          max: [3_000_000],
        },
      };
      this.dateConfig = {
        behaviour: 'drag',
        connect: 'lower',
        range: {
          min: [1, 1],
          max: 60,
        },
      };
    }
  }

  ngOnDestroy(): void {
    this.queryParamSub.unsubscribe();
  }

  get config(): IConfig {
    return this.type === SliderTypes.numeric
      ? this.numericConfig
      : this.dateConfig;
  }

  handleInputFocus(): void {
    this.inputFocused = true;
  }

  handleInputBlur(): void {
    const value = this.control.value;
    if (this.type === SliderTypes.numeric) {
      this.handleNumericBlur(value);
    } else {
      this.handleDateBlur(value);
    }

    this.inputFocused = false;
  }

  handleNumericBlur(value: number | number[]): void {
    if (value < this.minNumericValue) {
      this.control.setValue(this.minNumericValue, {
        onlySelf: true,
        emitEvent: false,
      });
    } else if (value > this.maxNumericValue) {
      this.control.setValue(this.maxNumericValue, {
        onlySelf: true,
        emitEvent: false,
      });
    }
  }

  handleDateBlur(value: number | number[]): void {
    if (value < 1) {
      this.control.setValue(1, { onlySelf: true, emitEvent: false });
    } else if (value > this.maxDateValue) {
      this.control.setValue(this.maxDateValue, {
        onlySelf: true,
        emitEvent: false,
      });
    }
  }

  handleSwitchToInput(): void {
    this.switchedToInput = true;
    this.control.setValidators([
      CurrencyInputMinimumValidator(this.minNumericValue, this.locale.locale),
    ]);
  }
}
